import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronRight, Download, FileJson, Loader2, RefreshCw } from "lucide-react";
import {
  buildCsv,
  buildJson,
  classifyFailure,
  detectPII,
  filterFailures,
  redactPII,
  type EvalFailure,
  type ExportRun,
} from "@/lib/helpdesk-eval-export";

// Drill-down view over persisted eval runs. Uses the shared helpers in
// src/lib/helpdesk-eval-export so the sanity-check vitest suite validates
// the same code path the UI + downloads run through.

type Run = ExportRun & {
  top_failures: EvalFailure[];
};

function downloadBlob(name: string, mime: string, body: string) {
  const blob = new Blob([body], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

export function HelpdeskEvalRunsPanel() {
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [intentFilter, setIntentFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  // Pagination is per-expanded-run: keeps the drill-down fast when a single
  // run contains hundreds of failures. Page resets whenever filters change.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(50);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("helpdesk_eval_runs")
      .select("id, created_at, suite, git_sha, branch, total, passed, failed, accuracy, baseline_accuracy, citation_pass_rate, injection_pass_rate, by_intent, top_failures")
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) toast.error(`Load runs: ${error.message}`);
    else {
      const parsed = ((data ?? []) as unknown as Run[]).map((r) => ({
        ...r,
        top_failures: (r.top_failures ?? []).map((f) => ({
          ...f,
          reason: redactPII(f.reason),
          answer_preview: redactPII(f.answer_preview),
        })),
      }));
      setRuns(parsed);
    }
    setLoading(false);
  };

  useEffect(() => { void load(); }, []);

  const intents = useMemo(() => {
    const s = new Set<string>();
    for (const r of runs) for (const f of r.top_failures) s.add(f.intent);
    return [...s].sort();
  }, [runs]);

  const filters = { category: categoryFilter, intent: intentFilter, search };
  const applyFilters = (fs: EvalFailure[]) => filterFailures(fs, filters);

  // Reset pagination whenever the filter set or expanded run changes.
  useEffect(() => { setPage(0); }, [categoryFilter, intentFilter, search, expanded, pageSize]);

  const exportRun = (r: Run, fmt: "csv" | "json") => {
    const failures = applyFilters(r.top_failures);
    const base = `helpdesk-eval-${new Date(r.created_at).toISOString().slice(0, 10)}-${r.id.slice(0, 8)}`;
    const body = fmt === "json" ? buildJson(r, failures, filters) : buildCsv(failures);

    // Defensive check: never download a file that still contains PII patterns.
    const leaks = detectPII(body);
    if (leaks.length) {
      toast.error(`Export blocked — residual PII detected (${leaks.join(", ")}). Contact eng.`);
      return;
    }

    downloadBlob(`${base}.${fmt}`, fmt === "json" ? "application/json" : "text/csv", body);
    toast.success(`Exported ${failures.length} failures as ${fmt.toUpperCase()}`);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <FileJson className="size-4 text-primary" /> Nightly eval runs — drill-down
          </CardTitle>
          <CardDescription>
            Persisted CI diff reports with the exact failing prompts, categorized by failure type.
            PII is stripped on both the CI side and again on render. Export any run as CSV or JSON.
          </CardDescription>
        </div>
        <Button variant="ghost" size="icon-sm" onClick={() => void load()} aria-label="Refresh">
          {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 md:grid-cols-[180px_180px_1fr]">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {["accuracy", "injection", "citation", "rls", "other"].map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={intentFilter} onValueChange={setIntentFilter}>
            <SelectTrigger><SelectValue placeholder="Intent" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All intents</SelectItem>
              {intents.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Search prompt id, reason, or answer preview…" value={search}
            onChange={(e) => setSearch(e.target.value)} />
        </div>

        <div className="space-y-2">
          {runs.length === 0 && !loading && (
            <p className="text-xs text-muted-foreground py-6 text-center">No eval runs recorded yet.</p>
          )}
          {runs.map((r) => {
            const isOpen = expanded === r.id;
            const passed = r.accuracy + 1e-9 >= r.baseline_accuracy;
            const filtered = applyFilters(r.top_failures);
            return (
              <div key={r.id} className="rounded-md border border-border">
                <button
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-accent/50 transition"
                  onClick={() => setExpanded(isOpen ? null : r.id)}
                >
                  <ChevronRight className={`size-4 transition ${isOpen ? "rotate-90" : ""}`} />
                  <span className="font-mono text-xs text-muted-foreground w-40">
                    {new Date(r.created_at).toISOString().replace("T", " ").slice(0, 16)}
                  </span>
                  <Badge variant="secondary">{r.suite}</Badge>
                  <Badge className={passed ? "bg-primary/15 text-primary hover:bg-primary/20" : ""}
                    variant={passed ? "default" : "destructive"}>
                    {(r.accuracy * 100).toFixed(1)}% / {(r.baseline_accuracy * 100).toFixed(0)}%
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {r.passed}/{r.total} passed · {filtered.length} matching failures
                  </span>
                  {r.git_sha && (
                    <span className="ml-auto font-mono text-[11px] text-muted-foreground">
                      {r.branch ?? ""} @ {r.git_sha.slice(0, 7)}
                    </span>
                  )}
                </button>

                {isOpen && (
                  <div className="border-t border-border p-3 space-y-3">
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => exportRun(r, "csv")}>
                        <Download className="size-3.5 mr-1" /> CSV
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => exportRun(r, "json")}>
                        <Download className="size-3.5 mr-1" /> JSON
                      </Button>
                      <span className="text-[11px] text-muted-foreground">
                        Exports reflect the current filters ({filtered.length} of {r.top_failures.length}).
                      </span>
                    </div>

                    {filtered.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-2">No failures match the current filters.</p>
                    ) : (
                      (() => {
                        const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
                        const clampedPage = Math.min(page, totalPages - 1);
                        const start = clampedPage * pageSize;
                        const pageRows = filtered.slice(start, start + pageSize);
                        return (
                          <>
                            <div className="space-y-2">
                              {pageRows.map((f) => {
                                const cat = classifyFailure(f);
                                return (
                                  <div key={f.id} className="rounded border border-border/60 bg-muted/30 p-2 text-xs">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <Badge variant="outline" className="font-mono">{f.id}</Badge>
                                      <Badge variant="secondary">{f.intent}</Badge>
                                      <Badge className={
                                        cat === "injection" ? "bg-destructive/15 text-destructive hover:bg-destructive/20"
                                        : cat === "rls" ? "bg-amber-500/15 text-amber-600 hover:bg-amber-500/20"
                                        : "bg-primary/15 text-primary hover:bg-primary/20"
                                      }>{cat}</Badge>
                                      {f.violations?.map((v) => (
                                        <Badge key={v} variant="outline" className="text-[10px]">{v}</Badge>
                                      ))}
                                    </div>
                                    <p className="mt-1 text-muted-foreground"><strong>Why:</strong> {f.reason || "—"}</p>
                                    {f.answer_preview && (
                                      <pre className="mt-1 whitespace-pre-wrap font-mono text-[11px] text-foreground/80 max-h-40 overflow-auto">{f.answer_preview}</pre>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 text-xs text-muted-foreground">
                              <span>
                                Showing {start + 1}–{Math.min(start + pageSize, filtered.length)} of {filtered.length}
                              </span>
                              <div className="flex items-center gap-2">
                                <label className="flex items-center gap-1">
                                  Rows per page
                                  <select
                                    className="bg-background border border-border rounded px-1 py-0.5"
                                    value={pageSize}
                                    onChange={(e) => setPageSize(Number(e.target.value))}
                                  >
                                    {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                                  </select>
                                </label>
                                <Button size="sm" variant="ghost" disabled={clampedPage === 0}
                                  onClick={() => setPage(Math.max(0, clampedPage - 1))}>Prev</Button>
                                <span className="font-mono">{clampedPage + 1}/{totalPages}</span>
                                <Button size="sm" variant="ghost" disabled={clampedPage >= totalPages - 1}
                                  onClick={() => setPage(clampedPage + 1)}>Next</Button>
                              </div>
                            </div>
                          </>
                        );
                      })()
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default HelpdeskEvalRunsPanel;
